import { LocationDescriptorObject } from 'history';
import { CourseId } from '../types/courses';
import { SectionId } from '../types/sections';
import * as queryString from 'querystring';
import { EventId } from '../../commonv3/types/events';
import { LessonId } from '../types/lessons';
import { SessionId } from '../../commonv3/types/sessions';
import { locatePath } from '../../common/RelativeUrl';
import { SessionType } from '../types/sessions';
import { CoachPositionType } from '../types/coachSkillCertifications';
import {
    ExamPracticalSectionId,
    ExamPracticalSectionSubmissionToken,
    ExamWritingSectionId,
} from '../../commonv3/types/courseExams';
import { TrialLessonId } from '../../admin/types/trial';
import { MembershipItemId } from '../../commonv3/types/membershipPlans';
import { ApplicationPageKey } from '../types/applicationPages';
import { GlobalStatesV3 } from '../../commonv3/states/GlobalStatesV3';
import { EventReservationId } from '../../commonv3/types/eventReservations';
import { SurveyKey, SurveyKeys } from '../../commonv3/types/surveys';
import { LessonHomeworkId } from '../../commonv3/types/lesson_homeworks';
import { LessonHomeworkSubmissionToken } from '../../commonv3/types/lesson_homework_submissions';
import { UserTutorialCompletionKey } from '../types/userTutorialCompletions';
import { SheEnv } from '../../commonv3/utils/envs';
import { NotificationDeliveryToken } from '../types/notification_deliveries';

export type EnhancedLocationDescriptor = LocationDescriptorObject & {
    stringify(): string;
    toAbsolute(): string;
};

function v3Path(path: string, params: Partial<LocationDescriptorObject> = {}): EnhancedLocationDescriptor {
    return {
        pathname: `/${path}`,
        stringify() {
            const search = this.search ? `?${this.search}` : '';
            const hash = this.hash ? this.hash : '';
            return `/${path}${hash}${search}`;
        },
        toAbsolute() {
            return `${location.origin}${this.stringify()}`;
        },
        ...params,
    };
}

export const toAnchor = (anchor) => () => {
    return { ...GlobalStatesV3.routing.location, hash: anchor ? `#${anchor}` : undefined };
};

export const V3Routes = {
    trial(anchor?: string) {
        return {
            pathname: '/trial',
            hash: anchor ? `#${anchor}` : undefined,
        };
    },
    trialDocument() {
        return {
            pathname: `/trial/document_01`,
        };
    },
    trialLessons() {
        return {
            pathname: `/trial_lessons`,
        };
    },
    trialLessonsMultiCreator() {
        return {
            pathname: `/trial_lessons/multicreator`,
        };
    },
    trialLessonReservationComplete(lessonId: TrialLessonId) {
        return {
            pathname: `/trial_lessons/${lessonId}/reservation_complete`,
        };
    },
    MultiCreatorTrialLessonReservationComplete(lessonId: TrialLessonId) {
        return {
            pathname: `/trial_lessons/multicreator/${lessonId}/reservation_complete`,
        };
    },
    trialLessonReschedule(reservationId: EventReservationId) {
        return {
            pathname: `/trial_reservations/${reservationId}/reschedule`,
        };
    },
    trialLessonRescheduleComplete(lessonId: TrialLessonId) {
        return {
            pathname: `/trial_lessons/${lessonId}/reschedule_complete`,
        };
    },
    localTrial(anchor?: string) {
        return {
            pathname: '/local_trial',
            hash: anchor ? `#${anchor}` : undefined,
        };
    },
    trialFaq() {
        return '/trial/faq';
    },
    pricing() {
        return '/pricing';
    },
    trialReservation(id: TrialLessonId) {
        return {
            pathname: `/trial_lessons/${id}/reservation`,
        };
    },
    application() {
        return v3Path('application');
    },
    applicationPageSummary(page_key: ApplicationPageKey) {
        return v3Path(`application_pages/${page_key}/apply`);
    },
    applicationPageUserInfo(page_key: ApplicationPageKey) {
        return v3Path(`application_pages/${page_key}/user_info`);
    },
    applicationPageSignIn(page_key: ApplicationPageKey) {
        return v3Path(`application_pages/${page_key}/sign_in`);
    },
    applicationPageUserSummary(page_key: ApplicationPageKey) {
        return v3Path(`application_pages/${page_key}/user_summary`);
    },
    applicationPageHomeLocationSelect(page_key: ApplicationPageKey) {
        return v3Path(`application_pages/${page_key}/home_location`);
    },
    applicationPageStartDateSelect(page_key: ApplicationPageKey) {
        return v3Path(`application_pages/${page_key}/start_date`);
    },
    applicationPagePaymentMethod(page_key: ApplicationPageKey) {
        return v3Path(`application_pages/${page_key}/payment_method`);
    },
    applicationPageConfirmation(page_key: ApplicationPageKey) {
        return v3Path(`application_pages/${page_key}/summary`);
    },
    beautyApplication() {
        return v3Path('apply/beauty');
    },
    beautyApplicationSummary() {
        return v3Path(`apply/beauty/apply`);
    },
    beautyApplicationUserInfo() {
        return v3Path(`apply/beauty/user_info`);
    },
    beautyApplicationSignIn() {
        return v3Path(`apply/beauty/sign_in`);
    },
    beautyApplicationUserSummary() {
        return v3Path(`apply/beauty/user_summary`);
    },
    beautyApplicationAddressInfo() {
        return v3Path(`apply/beauty/user_address`);
    },
    beautyApplicationHomeLocationSelect() {
        return v3Path(`apply/beauty/home_location`);
    },
    beautyApplicationStartDateSelect() {
        return v3Path(`apply/beauty/start_date`);
    },
    beautyApplicationPaymentMethod() {
        return v3Path(`apply/beauty/payment_method`);
    },
    beautyApplicationConfirmation() {
        return v3Path(`apply/beauty/summary`);
    },
    beautyApplicationComplete() {
        return v3Path(`apply/beauty/complete`);
    },
    applicationPageComplete(page_key: ApplicationPageKey) {
        return v3Path(`application_pages/${page_key}/complete`);
    },
    multicreatorApplication() {
        return v3Path('apply/multicreator');
    },
    multicreatorApplicationSummary() {
        return v3Path(`apply/multicreator/apply`);
    },
    multicreatorApplicationUserInfo() {
        return v3Path(`apply/multicreator/user_info`);
    },
    multicreatorApplicationSignIn() {
        return v3Path(`apply/multicreator/sign_in`);
    },
    multicreatorApplicationUserSummary() {
        return v3Path(`apply/multicreator/user_summary`);
    },
    multicreatorApplicationHomeLocationSelect() {
        return v3Path(`apply/multicreator/home_location`);
    },
    multicreatorApplicationStartDateSelect() {
        return v3Path(`apply/multicreator/start_date`);
    },
    multicreatorApplicationPackageSelect() {
        return v3Path(`apply/multicreator/package`);
    },
    multicreatorApplicationPaymentMethod() {
        return v3Path(`apply/multicreator/payment_method`);
    },
    multicreatorApplicationConfirmation() {
        return v3Path(`apply/multicreator/summary`);
    },
    multicreatorApplicationComplete() {
        return v3Path(`apply/multicreator/complete`);
    },
    multicreatorFull() {
        return v3Path(`full/multicreators`);
    },
    applicationSignIn() {
        return v3Path('application/sign_in');
    },
    applicationUserInfo() {
        return v3Path('application/user_info');
    },
    applicationStartDateSelect() {
        return v3Path('application/start_date');
    },
    applicationHomeLocationSelect() {
        return v3Path('application/home_location');
    },
    applicationPlanSelect() {
        return v3Path('application/plans');
    },
    applicationConfirmation() {
        return v3Path('application/summary');
    },
    applicationPaymentMethod() {
        return v3Path('application/payment_method');
    },
    applicationAddressInfo() {
        return v3Path('application/user_address');
    },
    applicationComplete() {
        return v3Path('application/complete');
    },
    applicationError() {
        return v3Path('application/error');
    },
    examWritingSection(sectionId: ExamWritingSectionId) {
        return v3Path(`exam_writing_sections/${sectionId}`);
    },
    examPracticalSection(sectionId: ExamPracticalSectionId) {
        return v3Path(`exam_practical_sections/${sectionId}`);
    },
    examPracticalSectionSubmissionShow(token: LessonHomeworkSubmissionToken) {
        return v3Path(`exam_practical_section_submissions/${token}`);
    },
    examPracticalSectionSubmissionShowForReviewer(token: ExamPracticalSectionSubmissionToken) {
        return v3Path(`reviewer/exam_practical_section_submissions/${token}`);
    },
    examPracticalSectionSubmissionNew(sectionId: ExamPracticalSectionId) {
        return v3Path(`exam_practical_sections/${sectionId}/submissions/new`);
    },
    examPracticalSectionSubmissionComplete(sectionId: ExamPracticalSectionId) {
        return v3Path(`exam_practical_sections/${sectionId}/submissions/complete`);
    },
    examPracticalSectionSubmissionsIndex(sectionId: ExamPracticalSectionId) {
        return v3Path(`exam_practical_sections/${sectionId}/submissions`);
    },
    eventArchives() {
        return v3Path(`event_archives`);
    },
    eventArchive(eventId: EventId) {
        return v3Path(`event_archives/${eventId}`);
    },
    home() {
        return v3Path('');
    },
    event(eventId: EventId) {
        return v3Path(`home/events/${eventId}`);
    },
    eventReservation(eventId: EventId) {
        return v3Path(`home/events/${eventId}/reservation`);
    },
    eventReservationComplete(eventId: EventId) {
        return v3Path(`home/events/${eventId}/reservation_complete`);
    },
    eventReservationError(eventId: EventId, message: string) {
        return v3Path(`home/events/${eventId}/reservation_error`, {
            search: message && queryString.stringify({ message }),
        });
    },
    coachingSessions() {
        return v3Path('home/coaching_sessions');
    },
    coachingSessionsIntroduction() {
        return v3Path('home/coaching_sessions_introduction');
    },
    sessions(sessionType: SessionType = SessionType.GROUP_COACHING) {
        return v3Path('home/sessions', {
            search: queryString.stringify({ sessionType }),
        });
    },
    careerCoachingSessions() {
        return v3Path(`home/career_coaching_sessions`);
    },
    sessionReservation(sessionId: SessionId) {
        return v3Path(`home/sessions/${sessionId}/reservation`);
    },
    sessionReservationComplete(sessionId: SessionId) {
        return v3Path(`home/sessions/${sessionId}/reservation_complete`);
    },
    sessionReservationError(sessionId: SessionId, message: string) {
        return v3Path(`home/sessions/${sessionId}/reservation_error`, {
            search: message && queryString.stringify({ message }),
        });
    },
    courses() {
        return v3Path('courses');
    },
    course(id: CourseId, preview: boolean = undefined) {
        return v3Path(`courses/${id}`, { search: preview && queryString.stringify({ preview }) });
    },
    lesson(id: CourseId, lessonId: LessonId) {
        return v3Path(`courses/${id}/lessons/${lessonId}`);
    },
    courseSection(id: CourseId, lessonId: LessonId, sectionId: SectionId) {
        return v3Path(`courses/${id}/lessons/${lessonId}/sections/${sectionId}`);
    },
    section(id: SectionId) {
        return v3Path(`sections/${id}`);
    },
    facebookSignIn(redirectUrl: string) {
        return locatePath(`/auth/facebook?${queryString.stringify({ from: redirectUrl })}`);
    },
    myPage() {
        return v3Path('home/mypage');
    },
    homeLocation() {
        return v3Path('home/mypage/home_location');
    },
    homeLocationUpdate() {
        return v3Path('home_location/update');
    },
    plan() {
        return v3Path('home/mypage/plans');
    },
    multiCreators() {
        return v3Path('home/mypage/multicreators');
    },
    planUpdate(currentItemId: MembershipItemId) {
        return v3Path(`membership_plans/${currentItemId}/update`);
    },
    planUpdatePlanSelect(currentItemId: MembershipItemId) {
        return v3Path(`membership_plans/${currentItemId}/update/plans`);
    },
    planUpdatePaymentMethod(currentItemId: MembershipItemId) {
        return v3Path(`membership_plans/${currentItemId}/update/payment_method`);
    },
    planUpdateConfirm(currentItemId: MembershipItemId) {
        return v3Path(`membership_plans/${currentItemId}/update/confirm`);
    },
    planUpdateComplete(currentItemId: MembershipItemId) {
        return v3Path(`membership_plans/${currentItemId}/update/complete`);
    },
    payments() {
        return v3Path('home/mypage/payments');
    },
    payment_info() {
        return v3Path('home/mypage/payment_info/');
    },
    retry_payment(code: string) {
        return v3Path(`home/mypage/retry_payment/${code}`);
    },
    generate_receipt(code: string) {
        return v3Path(`home/mypage/payments/${code}/receipt`);
    },
    receipt(code: string, name: string, purpose: string, appelation: string) {
        return v3Path(
            `payments/${code}/receipt/?${queryString.stringify({
                name: name,
                purpose: purpose,
                appelation: appelation,
            })}`
        );
    },
    password() {
        return v3Path('home/mypage/password');
    },
    email() {
        return v3Path('home/mypage/email');
    },
    profile() {
        return v3Path('home/mypage/profile');
    },
    reservations() {
        return v3Path('home/mypage/reservations');
    },
    reservationCancelComplete() {
        return v3Path('home/mypage/reservations/cancel_complete');
    },
    faq() {
        return v3Path('faq');
    },
    signOut() {
        return v3Path('sign_out');
    },
    signIn(redirect_url = undefined) {
        return v3Path('sign_in', {
            search: redirect_url ? queryString.stringify({ redirect_url }) : undefined,
        });
    },
    timesheetHome() {
        return v3Path('timesheet');
    },
    timesheetPositions(type?: CoachPositionType) {
        return v3Path('timesheet/positions', {
            search: type ? queryString.stringify({ type }) : undefined,
        });
    },
    timesheetComplete() {
        return v3Path('timesheet/complete');
    },
    timesheetError() {
        return v3Path('timesheet/error');
    },
    courseWorkSubmissions() {
        return v3Path('reviewer/course_work_submissions');
    },
    userCourseWorkSubmissions() {
        return v3Path('course_work_submissions');
    },
    communityService() {
        if (SheEnv.isProduction()) {
            return 'https://community.shelikes.jp';
        }
        if (SheEnv.isStaging()) {
            return 'https://staging.community.shelikes.jp';
        }
        return 'http://localhost:4000';
    },
    v2Migration() {
        return v3Path('migration');
    },
    goals() {
        return v3Path('home/goals');
    },
    idealCareer() {
        return v3Path('home/goals/ideal_career');
    },
    yearlyGoal() {
        return v3Path('home/goals/yearly');
    },
    monthlyGoal(year?: number, month?: number) {
        return v3Path('home/goals/monthly', {
            search: year && month && queryString.stringify({ year, month }),
        });
    },
    welcomeIntroduction() {
        return v3Path('home/welcome');
    },
    homeLocationIntroduction() {
        return v3Path('home/introduction/home_location');
    },
    homeLocationIntroductionComplete() {
        return v3Path('home/introduction/home_location/complete');
    },
    reserveCoaching() {
        return v3Path('home/introduction/coaching');
    },
    eventReservationAttendanceComplete(event_reservation_id: number) {
        return v3Path(`event_reservations/${event_reservation_id}/attendance/complete`);
    },
    reservationAttendanceComplete(reservation_id: number) {
        return v3Path(`reservations/${reservation_id}/attendance/complete`);
    },
    subscriptionSuspensionCancel(subscriptionCode: string, subscriptionSuspensionCode: string) {
        return v3Path(`subscription/${subscriptionCode}/suspension/${subscriptionSuspensionCode}/cancel`);
    },
    subscriptionSuspensionInfo(subscriptionCode: string) {
        return v3Path(`subscriptions/${subscriptionCode}/suspension`);
    },
    subscriptionSuspend(subscriptionCode: string) {
        return v3Path(`subscriptions/${subscriptionCode}/suspend`);
    },
    subscriptionSuspended(subscriptionCode: string) {
        return v3Path(`subscriptions/${subscriptionCode}/suspended`);
    },
    survey(key: SurveyKey, parameters: any = {}) {
        return v3Path(`surveys/${key}`, {
            search: queryString.stringify(parameters),
        });
    },
    trialLessonSurvey(id: TrialLessonId) {
        return this.survey(SurveyKeys.TRIAL_LESSON, { trial_lesson_id: id });
    },
    eventSurvey(id: EventId) {
        return this.survey(SurveyKeys.SPECIAL_EVENT, { event_id: id });
    },
    sessionSurvey(id: SessionId, sessionType: SessionType) {
        switch (sessionType) {
            case SessionType.GROUP_COACHING:
                return this.survey(SurveyKeys.GROUP_COACHING, { session_id: id });
            case SessionType.MOKUMOKU:
                return this.survey(SurveyKeys.MOKUMOKU, { session_id: id });
        }
        throw new Error('Unsupported sessionType : ' + sessionType);
    },
    userTutorialDetail(page_key: UserTutorialCompletionKey) {
        return v3Path(`home/user_tutorials/${page_key}`);
    },
    withdrawalApply() {
        return v3Path('withdrawal');
    },
    withdrawalForm() {
        return v3Path('withdrawal/form');
    },
    withdrawalComplete() {
        return v3Path('withdrawal/complete');
    },
    withdrawalCancel() {
        return v3Path('withdrawal/cancel');
    },
    subscriptionSuspensionCancelInfo(code: string) {
        return v3Path(`subscriptions/${code}/suspension/cancel`);
    },
    forgotPassword() {
        return v3Path(`passwords/forgot`);
    },
    validateResetCode() {
        return v3Path(`passwords/reset_code`);
    },
    resetPassword() {
        return v3Path(`passwords/reset`);
    },
    resetPasswordComplete() {
        return v3Path(`passwords/reset/complete`);
    },
    lessonHomeworkSubmission(homeworkId: LessonHomeworkId) {
        return v3Path(`lesson_homeworks/${homeworkId}/submissions/new`);
    },
    lessonHomeworkSubmissionShow(token: LessonHomeworkSubmissionToken) {
        return v3Path(`lesson_homework_submissions/${token}`);
    },
    lessonHomeworkSubmissionShowForReviewer(token: LessonHomeworkSubmissionToken) {
        return v3Path(`reviewer/lesson_homework_submissions/${token}`);
    },
    lessonHomeworkSubmissionComplete(homeworkId: LessonHomeworkId) {
        return v3Path(`lesson_homeworks/${homeworkId}/submissions/complete`);
    },
    lessonHomeworkSubmissionsIndex(homeworkId: LessonHomeworkId) {
        return v3Path(`lesson_homeworks/${homeworkId}/submissions`);
    },
    NotificationDeliveryShowPage(token: NotificationDeliveryToken) {
        return v3Path(`notifications/${token}`);
    },
    NotificationDeliveryListPage() {
        return v3Path(`notifications`);
    },
};
